import { format, isAfter } from 'date-fns';
import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import {
  DataModelTransition,
  DataModelTransitionParams,
} from '../domain/dataModelTransition';
import { DataModelTransitionGraphParams } from '../domain/dataModelTransitionGraph';
import { resolveDataModelTransitionGraphFieldsFromFavorite } from '../domain/dataModelTransitionGraphFields';
import { FavoriteItem } from '../domain/favorites';
import { MODEL_REPORT_MARKING_CONDITIONS } from '../domain/marking';
import { resolveFavoriteFilteredKiTagIds } from '../domain/modelTransition/favoriteFilteredKiTagIds';
import {
  applyModelTransitionCurrentShuToSearchCondition,
  resolveModelTransitionDefaultNonTransitiveFields,
  resolveModelTransitionFetchHalls,
  resolveModelTransitionMainField,
  sanitizeModelTransitionSelectedFieldCodes,
  syncModelTransitionGraphParamsWithSearchCondition,
} from '../domain/modelTransition/mainField';
import { ModelTransitionFavorite } from '../domain/modelTransition/types';
import { ModelTransitionReportsSettingDateRangeParams } from '../domain/modelTransitionReportsSettingDateRangeParams';
import { Column, MAIN_FIELD_TYPE, SearchCondition } from '../domain/schemas';
import { SettingsOptionsModelTransition } from '../domain/settingsOptionsModelTransition';
import { ShuOption } from '../domain/shu';
import { resolveFavoriteFilteredSisTypeCodes } from '../domain/sis/favoriteFilteredSisTypeCodes';

import { SIS_TYPE_UPDATE_DATES } from '../consistents';
import {
  DataModelTransitionActionCreators,
  dataModelTransitionColumnsForNonTransitionSelector,
  dataModelTransitionColumnsForTransitionSelector,
  dataModelTransitionSelector,
  dataModelTransitionSettingsSelector,
} from '../redux/server/dataModelTransition';
import {
  DataModelTransition2ndRowActionCreators,
  dataModelTransition2ndRowSettingsSelector,
} from '../redux/server/dataModelTransition2ndRow';
import {
  DataModelTransitionGraphActionCreators,
  dataModelTransitionGraphSelectedKiListSelector,
  dataModelTransitionGraphSettingSelector,
} from '../redux/server/dataModelTransitionGraph';
import {
  SettingsOptionsKiActionCreators,
  SettingsOptionsKiActionTypes,
  settingsOptionsKiLoadingSelector,
  settingsOptionsKiSelector,
} from '../redux/server/settingsOptionsKi';
import {
  SettingsOptionsModelTransitionActionCreators,
  SettingsOptionsModelTransitionActionTypes,
  settingsOptionsModelTransitionFieldsSelector,
  settingsOptionsModelTransitionLoadingStateSelector,
  settingsOptionsModelTransitionRawSelector,
  settingsOptionsModelTransitionSearchConditionSelector,
} from '../redux/server/settingsOptionsModelTransition';
import { ShortenedUrlActionCreators } from '../redux/server/shortenedUrl';
import { ErrorActionCreators } from '../redux/ui/error';
import {
  ChangeModelTransitionReportsCurrentHallsAction,
  ChangeModelTransitionReportsCurrentShuAction,
  CreateModelTransitionReportsShortenedUrlAction,
  ModelTransitionReportsSettingActionCreators,
  ModelTransitionReportsSettingActionTypes,
  ModelTransitionReportsSettingState,
  SaveModelTransitionReportsFavorite,
  SearchModelTransitionReportsAction,
  SearchModelTransitionReportsDateRangeSlideAction,
  SearchModelTransitionReportsFieldsAction,
  SearchModelTransitionReportsMarkingAction,
  SearchModelTransitionReportsSortForHeaderAction,
  SearchModelTransitionReportsSortForTransitiveFieldAction,
  modelTransitionCurrentFieldCodesSelector,
  modelTransitionCustomDateSelector,
  modelTransitionCustomMonthSelector,
  modelTransitionCustomWeekSelector,
  modelTransitionReportsCheckedKiListTableSelector,
  modelTransitionReportsColumnsForNonTransitionOrderSelector,
  modelTransitionReportsColumnsForTransitionOrderSelector,
  modelTransitionReportsFilteredKiTagIdsSelector,
  modelTransitionReportsFilteredSisTypeCodesSelector,
  modelTransitionReportsModelNameFilterSelector,
  modelTransitionReportsPaginationSelector,
  modelTransitionReportsSearchConditionSelector,
  modelTransitionReportsSelectedCurrentShuSelector,
  modelTransitionReportsSelectedDateRangeParamsSelector,
  modelTransitionReportsSelectedFavoriteDataSelector,
  modelTransitionReportsSelectedFavoritePageSettingSelector,
  modelTransitionReportsSelectedFavoriteSelector,
  modelTransitionReportsSettingCurrentHallsSelector,
  modelTransitionReportsSettingIsHeatmapEnabledSelector,
  modelTransitionReportsSettingSelector,
  modelTransitionReportsTableFilterSelector,
  selectModelTransitionCheckedKiListTableAction,
  selectModelTransitionReportsColumnsForNonTransitionAction,
  selectModelTransitionReportsColumnsForTransitionAction,
  selectModelTransitionReportsFilteredKiTagIdsAction,
  selectModelTransitionReportsFilteredSisTypeCodesAction,
  selectModelTransitionReportsModelNameFilterAction,
} from '../redux/ui/modelTransitionReportsSetting';
import { SettingsFavoritesActionCreators } from '../redux/ui/settingsFavorites';
import { compressToEncodedURIComponent } from '../utils/compressToEncodedURIComponent';
import { customSelect } from '../utils/customSelect';
import { dataModelTransitionDefaultValue } from '../utils/dataModelTransitionDefaultValue';
import { isValidArea } from '../utils/isValidArea';
import {
  checkCurrentShu,
  getShuOption,
  selectShus2HallReportSearchCondition,
} from '../utils/shu';
import { sortBy } from '../utils/sortBy';
import {
  CUSTOM_DATE_RANGE,
  DATE_RANGE_XX_DAY,
  DATE_RANGE_XX_MONTH,
  DATE_RANGE_XX_WEEK,
  TEMPLATE_CONVERSION_CONFIG,
  TransitiveDateRange,
  calcStartEndDate,
  calcStartEndDateForModelTransition,
  dialyTransitiveDateRanges,
  monthlyTransitiveDateRangesForModelTransition,
  weeklyTransitiveDateRanges,
} from '../utils/transitiveDateRange';
import { updateSisTypesV1 } from '../utils/updateSisTypes';

/**
 * 選択中の種別の情報を元に検索条件を加工する
 * @param currentShu 選択中の種別の情報
 * @param searchParams 期間推移の検索条件
 * @returns 加工された検索条件
 */
export const queryParameterToSearchParams = (
  currentShu: ShuOption,
  searchParams: DataModelTransitionParams
): DataModelTransitionParams => {
  const selectedShuParams = selectShus2HallReportSearchCondition([currentShu]);
  // 必要に応じて検索パラメータを加工する
  return {
    ...searchParams,
    // 選択中の種別の情報を加える
    ...selectedShuParams,
    // MEMO: ForSijiritu 系のパラメータは検索条件で指定されているものを分母として渡す
    ...{
      kiListForSijiritu: searchParams.kiList,
    },
  };
};

// 検索条件に非推移項目があるかどうか判定する
export const isExistNonTransitiveField = (
  searchCondition: DataModelTransitionParams
) => {
  return searchCondition.nonTransitiveFields !== undefined
    ? searchCondition.nonTransitiveFields.length === 0
      ? false
      : true
    : false;
};

// 有効な期間指定
const VALID_TRANSITIVE_DATE_RANGES_SET = new Set<TransitiveDateRange>([
  ...dialyTransitiveDateRanges,
  ...weeklyTransitiveDateRanges,
  ...monthlyTransitiveDateRangesForModelTransition,
  DATE_RANGE_XX_DAY,
  DATE_RANGE_XX_WEEK,
  DATE_RANGE_XX_MONTH,
]);

/**
 * TransitiveDateRangeの型ガード関数
 * @param value チェックする値
 * @returns valueがTransitiveDateRange型かどうか
 */
function isValidTransitiveDateRange(
  value: string
): value is TransitiveDateRange {
  return VALID_TRANSITIVE_DATE_RANGES_SET.has(value as TransitiveDateRange);
}

/**
 * 期間指定を変換する関数
 * テンプレート期間（「7日」「13週」など）を「期間xx日/週/ヶ月」に変換する
 * @param originDateRange 元の期間指定
 * @param dateType 日付タイプ（'daily' | 'weekly' | 'monthly' | 'any'）
 * @returns 変換後の期間指定
 */
function convertDateRange(
  originDateRange: string,
  dateType: 'daily' | 'weekly' | 'monthly' | 'any'
): TransitiveDateRange {
  // 指定した期間が有効かどうか検証
  if (!isValidTransitiveDateRange(originDateRange)) {
    throw new Error(
      `Invalid date range: ${originDateRange} (dateType: ${dateType})`
    );
  }

  // 'any'の場合は変換不要
  if (dateType === 'any') {
    return originDateRange;
  }

  // テンプレート変換設定を取得
  const config = TEMPLATE_CONVERSION_CONFIG[dateType];

  // テンプレートに一致するかチェック
  const isConvertible = config.templates.some(
    (value) => originDateRange === `${value}${config.suffix}`
  );

  // 変換可能な場合は「期間xx日/週/ヶ月」に変換、そうでなければそのまま返す
  return isConvertible
    ? config.customRange // 「期間xx日/週/ヶ月」
    : originDateRange; // そのまま（「カスタム」など）
}

/**
 * 指定した期間から日付範囲を算出する関数
 * @param originDateRange 元の期間指定
 * @param originDateType 日付タイプ（'daily' | 'weekly' | 'monthly' | 'any'）
 * @param customDate カスタム日数値
 * @param customWeek カスタム週数値
 * @param customMonth カスタム月数値
 * @returns 算出された日付範囲、またはカスタム期間の場合はundefined
 */
function calculateDateFromDateRange(
  originDateRange: string,
  originDateType: 'daily' | 'weekly' | 'monthly' | 'any',
  customDate: string,
  customWeek: string,
  customMonth: string
): ReturnType<typeof calcStartEndDate> | undefined {
  // 指定した期間を変換
  // 例：「7週」などを「期間xx週」に変換
  const convertedDateRange = convertDateRange(originDateRange, originDateType);

  // 期間に応じて再計算処理を分岐
  switch (convertedDateRange) {
    case CUSTOM_DATE_RANGE:
      // カスタム期間の場合は保存された日付を使用(再計算なし)
      return undefined;

    case DATE_RANGE_XX_DAY:
    case DATE_RANGE_XX_WEEK:
    case DATE_RANGE_XX_MONTH:
      // 期間xx日/週/ヶ月の場合、カスタム入力値を使用して計算
      return calcStartEndDateForModelTransition(
        originDateType,
        convertedDateRange,
        new Date(),
        { customDate, customWeek, customMonth }
      );

    // 固定期間のケース
    case '7日':
    case '14日':
    case '7週':
    case '13週':
    case '3ヶ月':
    case '7ヶ月':
    case '13ヶ月':
      // 固定期間から日付を算出: 通常の計算ロジック
      return calcStartEndDate(originDateType, convertedDateRange);

    default:
      // 定義外の値が来た場合はエラー
      throw new Error(
        `未定義の期間タイプが指定されました: ${convertedDateRange} (dateType: ${originDateType})`
      );
  }
}

/**
 * 期間パラメータから日付範囲を計算し、検索パラメータを更新する関数
 * @param dateRangeParams 期間パラメータ
 * @param customDate カスタム日数値
 * @param customWeek カスタム週数値
 * @param customMonth カスタム月数値
 * @param searchCondition 元の検索条件
 * @returns 日付が更新された検索パラメータ
 */
function updateSearchParamsWithDateRange(
  dateRangeParams: ModelTransitionReportsSettingDateRangeParams,
  customDate: string | undefined,
  customWeek: string | undefined,
  customMonth: string | undefined,
  searchCondition: DataModelTransitionParams
): DataModelTransitionParams {
  // カスタム期間以外の場合は日付を再計算
  if (dateRangeParams.dateRange === CUSTOM_DATE_RANGE) {
    return searchCondition;
  }

  let date: ReturnType<typeof calcStartEndDate>;

  // 期間xx日/週/ヶ月の場合は、カスタム値を使用して計算
  if (
    dateRangeParams.dateRange === DATE_RANGE_XX_DAY ||
    dateRangeParams.dateRange === DATE_RANGE_XX_WEEK ||
    dateRangeParams.dateRange === DATE_RANGE_XX_MONTH
  ) {
    // カスタム値を使用して日付を計算
    date = calcStartEndDateForModelTransition(
      dateRangeParams.dateType,
      dateRangeParams.dateRange,
      new Date(),
      {
        customDate: customDate ?? TEMPLATE_CONVERSION_CONFIG.daily.defaultValue,
        customWeek:
          customWeek ?? TEMPLATE_CONVERSION_CONFIG.weekly.defaultValue,
        customMonth:
          customMonth ?? TEMPLATE_CONVERSION_CONFIG.monthly.defaultValue,
      }
    );
  } else {
    // 固定期間から日付を算出: 通常の計算ロジック
    date = calcStartEndDate(
      dateRangeParams.dateType,
      dateRangeParams.dateRange
    );
  }

  const startDate = format(date.startDate, 'yyyy-MM-dd');
  const endDate = format(date.endDate, 'yyyy-MM-dd');
  return { ...searchCondition, startDate, endDate };
}

/**
 * お気に入り選択時、データを取得する
 */
function* applyFavoriteByIdSaga() {
  const favoriteItem: FavoriteItem | undefined = yield select(
    modelTransitionReportsSelectedFavoriteDataSelector
  );
  // 現在選択中のお気に入りデータを取得
  const favorite: ModelTransitionFavorite | undefined = yield select(
    modelTransitionReportsSelectedFavoritePageSettingSelector
  );

  // デフォルトのお気に入りを選択した場合、リセットと同じ動作を行う
  if (favorite === undefined || favoriteItem === undefined) {
    yield put(
      ModelTransitionReportsSettingActionCreators.searchResetModelTransitionReportsAction()
    );
    return;
  }

  const lastUpdateAt = favoriteItem.updatedAt ?? favoriteItem.createdAt;
  if (lastUpdateAt == null) {
    throw new Error('お気に入りの作成日が存在しません');
  }

  // 機種別推移のデータを初期化する
  // お気に入り適用前に既存データをクリアして、次のfetchを明確にする
  yield put(DataModelTransitionActionCreators.renewDataModelTransitionAction());
  yield fork(initModelTransitionReportsDataSaga);

  yield fork(applyFavoriteSaga, favorite, lastUpdateAt);
}

/**
 * お気に入りのタグ絞込 ID を現行マスタで絞り込む。
 *
 * settings/options/ki 未取得なら取得完了を待つ。未取得（または取得失敗）では保存 ID を残し、
 * 取得済みでタグ 0 件のときだけ空配列にする。
 */
export function* resolveModelTransitionFavoriteFilteredKiTagIdsSaga(
  savedKiTagIds: number[] | undefined
) {
  if (savedKiTagIds == null || savedKiTagIds.length === 0) {
    return [];
  }

  const loadingState = yield* customSelect(settingsOptionsKiLoadingSelector);
  if (loadingState !== 'loaded') {
    if (loadingState === 'prepare') {
      yield put(SettingsOptionsKiActionCreators.fetchSettingsOptionsKiAction());
    }

    const result: { success?: unknown; reset?: unknown } = yield race({
      success: take(
        SettingsOptionsKiActionTypes.FETCH_SETTINGS_OPTIONS_KI_SUCCESS
      ),
      reset: take(SettingsOptionsKiActionTypes.RENEW_SETTINGS_OPTIONS_KI),
    });

    if (result.success == null) {
      console.warn(
        '[modelTransitionReportsSagas] settingsOptionsKi の取得に失敗したためタグ絞り込みのマスタ照合をスキップ'
      );
      return savedKiTagIds;
    }
  }

  const settingsOptionsKi = yield* customSelect(settingsOptionsKiSelector);
  if (settingsOptionsKi == null) {
    console.warn(
      '[modelTransitionReportsSagas] settingsOptionsKi を参照できないためタグ絞り込みのマスタ照合をスキップ'
    );
    return savedKiTagIds;
  }

  return resolveFavoriteFilteredKiTagIds(
    savedKiTagIds,
    settingsOptionsKi.searchCondition.kiTagList
  );
}

/**
 * お気に入りの SIS タイプ絞り込みを現行マスタで照合する。
 *
 * settings/options/kiTransition 未取得なら取得完了を待つ。未取得（または取得失敗）では保存コードを残し、
 * 取得済みで SIS タイプ 0 件のときだけ空配列にする。
 */
export function* resolveModelTransitionFavoriteFilteredSisTypeCodesSaga(
  savedSisTypeCodes: string[] | undefined
) {
  if (savedSisTypeCodes == null || savedSisTypeCodes.length === 0) {
    return [];
  }

  const loadingState = yield* customSelect(
    settingsOptionsModelTransitionLoadingStateSelector
  );
  if (loadingState !== 'loaded') {
    if (loadingState === 'prepare') {
      yield put(
        SettingsOptionsModelTransitionActionCreators.fetchSettingsOptionsModelTransitionAction()
      );
    }

    const result: { success?: unknown; reset?: unknown } = yield race({
      success: take(
        SettingsOptionsModelTransitionActionTypes.FETCH_SETTINGS_OPTIONS_MODEL_TRANSITION_SUCCESS
      ),
      reset: take(
        SettingsOptionsModelTransitionActionTypes.RENEW_SETTINGS_OPTIONS_MODEL_TRANSITION
      ),
    });

    if (result.success == null) {
      console.warn(
        '[modelTransitionReportsSagas] settingsOptionsModelTransition の取得に失敗したため SIS タイプ絞り込みのマスタ照合をスキップ'
      );
      return savedSisTypeCodes;
    }
  }

  const settingsOptionsModelTransition = yield* customSelect(
    settingsOptionsModelTransitionRawSelector
  );
  if (settingsOptionsModelTransition == null) {
    console.warn(
      '[modelTransitionReportsSagas] settingsOptionsModelTransition を参照できないため SIS タイプ絞り込みのマスタ照合をスキップ'
    );
    return savedSisTypeCodes;
  }

  return resolveFavoriteFilteredSisTypeCodes(
    savedSisTypeCodes,
    settingsOptionsModelTransition.searchCondition.sisTypes
  );
}

/**
 * ModelTransitionFavoriteの内容を実際に反映させる
 */
export function* applyFavoriteSaga(
  favorite: ModelTransitionFavorite,
  lastUpdatedAt: string
) {
  // 選択中の種別に反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsCurrentShuAction(
      favorite.currentShu
    )
  );
  // 選択中の種別一覧に反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsShuAction(
      favorite.selectedShu
    )
  );
  // 選択中の機種リストに反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsKiListAction(
      favorite.kiList
    )
  );
  // 選択中のSISリストに反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSisListAction(
      favorite.selectedSis
    )
  );

  const fieldCodesForFavorite =
    favorite.selectedCurrentFieldCodes != null &&
    favorite.selectedCurrentFieldCodes.length > 0
      ? favorite.selectedCurrentFieldCodes
      : favorite.selectedCurrentFieldCode != null
      ? [favorite.selectedCurrentFieldCode]
      : ['all'];

  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionCurrentFieldAction(
      fieldCodesForFavorite
    )
  );

  // ページネーションの状態を更新
  if (favorite.pagination) {
    if (favorite.pagination.main) {
      yield put(
        ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsPaginationAction(
          'main',
          favorite.pagination.main.page,
          favorite.pagination.main.rowsPerPage
        )
      );
    }
  }

  // お気に入りから期間と推移タイプを取得
  const originDateRange = favorite.selectedDateRangeParams.dateRange;
  const originDateType = favorite.selectedDateRangeParams.dateType;

  // テンプレート変換関数の定義
  // 「7日」→「7」、「13週」→「13」などに変換し、テンプレートに一致しない場合は null を返す
  const convertTemplateToCustomValue = (
    dateRange: string,
    dateType: 'daily' | 'weekly' | 'monthly'
  ): string | null => {
    const config = TEMPLATE_CONVERSION_CONFIG[dateType];
    for (const value of config.templates) {
      if (dateRange === `${value}${config.suffix}`) {
        return value;
      }
    }
    return null;
  };

  // 日次のテンプレート変換
  // 例：originDateRange = "7日"の場合、convertedDaily = "7"→customDate = "7"に変換
  const convertedDaily = convertTemplateToCustomValue(originDateRange, 'daily');
  // カスタム値の初期化 (テンプレート変換値 > お気に入り保存値 > デフォルト値の優先順位)
  const customDate =
    convertedDaily ??
    favorite.customDate ??
    TEMPLATE_CONVERSION_CONFIG.daily.defaultValue;

  // 週次のテンプレート変換
  // 例：originDateRange = "13週"の場合、convertedWeekly = "13"→customWeek = "13"に変換
  const convertedWeekly = convertTemplateToCustomValue(
    originDateRange,
    'weekly'
  );
  const customWeek =
    convertedWeekly ??
    favorite.customWeek ??
    TEMPLATE_CONVERSION_CONFIG.weekly.defaultValue;

  // 月次のテンプレート変換
  // 例：originDateRange = "3ヶ月"の場合、convertedMonthly = "3"→customMonth = "3"に変換
  const convertedMonthly = convertTemplateToCustomValue(
    originDateRange,
    'monthly'
  );
  const customMonth =
    convertedMonthly ??
    favorite.customMonth ??
    TEMPLATE_CONVERSION_CONFIG.monthly.defaultValue;

  // Reduxにカスタム値を保存
  yield put(
    ModelTransitionReportsSettingActionCreators.changeModelTransitionCustomDateAction(
      customDate
    )
  );
  yield put(
    ModelTransitionReportsSettingActionCreators.changeModelTransitionCustomWeekAction(
      customWeek
    )
  );
  yield put(
    ModelTransitionReportsSettingActionCreators.changeModelTransitionCustomMonthAction(
      customMonth
    )
  );

  // 期間指定を変換
  // 例：「7週」などを「期間xx週」に変換
  const convertedDateRange = convertDateRange(originDateRange, originDateType);

  // 期間を元にstartDateとendDateを算出
  const date = calculateDateFromDateRange(
    originDateRange,
    originDateType,
    customDate,
    customWeek,
    customMonth
  );

  // 期間を再算出して上書きする
  // dateが算出されていない場合はカスタムと判断し、保持されている検索パラメータの値をそのまま送る
  const startDate =
    date !== undefined
      ? format(date.startDate, 'yyyy-MM-dd')
      : favorite.searchCondition.startDate;
  const endDate =
    date !== undefined
      ? format(date.endDate, 'yyyy-MM-dd')
      : favorite.searchCondition.endDate;

  const sisTypes = isAfter(
    new Date(lastUpdatedAt),
    new Date(SIS_TYPE_UPDATE_DATES.v1)
  )
    ? favorite.searchCondition.sisTypes
    : updateSisTypesV1(favorite.searchCondition.sisTypes);

  if (!isValidArea(favorite.searchCondition.areas)) {
    yield put(
      ErrorActionCreators.setError(
        `お気に入りの検索条件に存在しないエリアが指定されています。\n正しいエリアを選択して検索を再度行い、お気に入りに保存してください。`
      )
    );
  }

  const favoriteSearchCondition: DataModelTransitionParams = {
    ...favorite.searchCondition,
    startDate,
    endDate,
    ...(sisTypes ? { sisTypes } : {}),
    mainField:
      favorite.searchCondition.mainField ?? MAIN_FIELD_TYPE.KI_TUSHO_MEI,
  };

  const hallsForFetch = resolveModelTransitionFetchHalls(
    favorite.currentHalls,
    favoriteSearchCondition.halls
  );

  // 選択中の検索条件に反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSearchConditionAction(
      favoriteSearchCondition
    )
  );
  // dateType・dateRange（選択中の日付タイプ・期間）を更新する
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsDateRangeParamsAction(
      originDateType,
      convertedDateRange
    )
  );

  // 機種別推移テーブルを取得
  if (favorite.dataModelTransition != null) {
    yield put(
      DataModelTransitionActionCreators.fetchDataModelTransitionAction(
        favorite.currentShu,
        {
          ...dataModelTransitionDefaultValue().setting,
          ...favorite.dataModelTransition,
          startDate,
          endDate,
          ...(sisTypes ? { sisTypes } : {}),
          halls: hallsForFetch,
        }
      )
    );

    // テーブル列の推移項目並び順を変更
    if (favorite.columnForTransitionOrder) {
      yield put(
        ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsColumnsForTransitionAction(
          favorite.columnForTransitionOrder
        )
      );
    }

    // テーブル列の非推移項目並び順を変更
    if (favorite.columnForNonTransitionOrder) {
      yield put(
        ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsColumnsForNonTransitionAction(
          favorite.columnForNonTransitionOrder
        )
      );
    }

    // 機種名フィルタを変更
    yield put(
      selectModelTransitionReportsModelNameFilterAction(
        favorite.modelNameFilter
      )
    );
    // タグ絞り込みを変更（削除済みタグは除外して復元する）
    const filteredKiTagIds =
      yield* resolveModelTransitionFavoriteFilteredKiTagIdsSaga(
        favorite.filteredKiTagIds
      );
    yield put(
      selectModelTransitionReportsFilteredKiTagIdsAction(filteredKiTagIds)
    );
    const filteredSisTypeCodes =
      yield* resolveModelTransitionFavoriteFilteredSisTypeCodesSaga(
        favorite.filteredSisTypeCodes
      );
    yield put(
      selectModelTransitionReportsFilteredSisTypeCodesAction(
        filteredSisTypeCodes
      )
    );

    // 店舗ドロップダウンを変更
    yield put(
      ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsCurrentHallsAction(
        favorite.currentHalls ?? []
      )
    );

    // ヒートマップ表示の有効無効
    yield put(
      ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsIsHeatmapEnabledAction(
        resolveModelTransitionMainField(favoriteSearchCondition.mainField) ===
          MAIN_FIELD_TYPE.KI_TUSHO_MEI &&
          (favorite.isHeatmapEnabled ?? false)
      )
    );
  }

  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsTableHeaderSubMenuMultiFilterAction(
      favorite.tableHeaderSubMenuMultiFilter ?? {}
    )
  );

  // 検索条件 → エフェクトクリエイター（データ取得）に変換
  const dataModelTransition2ndRow = favorite.dataModelTransition2ndRow;
  if (dataModelTransition2ndRow !== undefined) {
    // Fetchする展開行一覧を生成
    const tasks2ndRow = Object.keys(dataModelTransition2ndRow).map((rowId) => {
      return put(
        DataModelTransition2ndRowActionCreators.fetchDataModelTransition2ndRowAction(
          rowId,
          {
            ...dataModelTransition2ndRow[rowId],
            startDate,
            endDate,
            halls: hallsForFetch,
          }
        )
      );
    });
    // 全件取得
    yield all(tasks2ndRow);
  }

  // テーブルフィルター項目を取得する
  // 項目一覧を反映する
  yield put(
    ModelTransitionReportsSettingActionCreators.applyModelTransitionReportsTableFilterAction(
      favorite.tableFilterItems ?? []
    )
  );

  // テーブルでチェックされた機種を反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionCheckedKiListTableAction(
      favorite.checkedKiListTable || {}
    )
  );

  // 推移グラフ（機種比較）を取得（setting.fields → setting.field → デフォルト）
  if (favorite.dataModelTransitionGraph !== undefined) {
    const { setting } = favorite.dataModelTransitionGraph;
    const graphFields =
      resolveDataModelTransitionGraphFieldsFromFavorite(setting);
    // WHY: テーブル fetch は currentShu で種別を上書きするが、searchCondition.shuGroupIds は
    // 種別ドロップダウン変更後も古いまま。グラフだけ UI 検索条件を使うと空になる（P10）
    yield put(
      DataModelTransitionGraphActionCreators.fetchDataModelTransitionGraphAction(
        syncModelTransitionGraphParamsWithSearchCondition(
          { ...setting, fields: graphFields },
          applyModelTransitionCurrentShuToSearchCondition(
            {
              ...favoriteSearchCondition,
              halls: hallsForFetch,
            },
            favorite.currentShu
          )
        )
      )
    );
    // グラフの機種選択を適用
    yield put(
      DataModelTransitionGraphActionCreators.changeDataModelTransitionGraphKiAction(
        favorite.dataModelTransitionGraph.selectKi
      )
    );
  } else {
    yield put(
      DataModelTransitionGraphActionCreators.renewDataModelTransitionGraphAction()
    );
  }
}

/**
 * お気に入りに保存するデータの生成
 */
function* setPageSetting() {
  // 現在の機種別推移の設定すべて
  const pageSetting: ModelTransitionReportsSettingState = yield select(
    modelTransitionReportsSettingSelector
  );

  // 現在の機種別推移テーブルの検索条件
  const dataModelTransition: DataModelTransitionParams | undefined =
    yield select(dataModelTransitionSettingsSelector);
  // 現在の機種別推移テーブルの展開行の検索条件
  const dataModelTransition2ndRow:
    | {
        [key: string]: DataModelTransitionParams;
      }
    | undefined = yield select(dataModelTransition2ndRowSettingsSelector);
  // 種別選択前の時は動作しない
  if (pageSetting.currentShu === undefined) return;

  // 機種名フィルタを取得
  const modelNameFilter: string = yield select(
    modelTransitionReportsModelNameFilterSelector
  );

  // 機種タグ別出力のタグ絞り込みを取得
  const filteredKiTagIds = yield* customSelect(
    modelTransitionReportsFilteredKiTagIdsSelector
  );
  const filteredSisTypeCodes = yield* customSelect(
    modelTransitionReportsFilteredSisTypeCodesSelector
  );

  // テーブルフィルターで選択中の項目
  const tableFilterSelectedItems: string[] | undefined = yield select(
    modelTransitionReportsTableFilterSelector
  );

  // テーブルでチェックされた機種の種別ごとのテーブルを取得
  const checkedKiListTable: { [shuCode: string]: string[] } = yield select(
    modelTransitionReportsCheckedKiListTableSelector
  );

  // 現在の推移グラフ（機種比較）の検索条件
  const dataModelTransitionGraphParams:
    | DataModelTransitionGraphParams
    | undefined = yield select(dataModelTransitionGraphSettingSelector);
  // 現在の推移グラフ（機種比較）の選択機種
  const selectKi: string[] = yield select(
    dataModelTransitionGraphSelectedKiListSelector
  );

  // 店舗フィルタで選択中の項目
  const currentHalls: string[] = yield select(
    modelTransitionReportsSettingCurrentHallsSelector
  );

  // ヒートマップの有効無効
  const isHeatmapEnabled: boolean = yield select(
    modelTransitionReportsSettingIsHeatmapEnabledSelector
  );

  const currentFieldCodes: string[] = yield select(
    modelTransitionCurrentFieldCodesSelector
  );

  // ページネーションの状態を取得
  const pagination = yield* customSelect(
    modelTransitionReportsPaginationSelector
  );

  // カスタム日数を取得
  const customDate = yield* customSelect(modelTransitionCustomDateSelector);

  // カスタム週数を取得
  const customWeek = yield* customSelect(modelTransitionCustomWeekSelector);

  // カスタム月数を取得
  const customMonth = yield* customSelect(modelTransitionCustomMonthSelector);

  const result: FavoriteItem['pageSetting'] = {
    modelTransition: {
      searchCondition: pageSetting.searchCondition,
      selectedShu: pageSetting.selectedShu,
      selectedSis: pageSetting.selectedSis,
      currentShu: pageSetting.currentShu,
      selectedDateRangeParams: pageSetting.selectedDateRangeParams,
      ...(dataModelTransition ? { dataModelTransition } : {}),
      ...(dataModelTransition2ndRow ? { dataModelTransition2ndRow } : {}),
      kiList: pageSetting.kiList ?? [],
      columnForTransitionOrder: pageSetting?.columnsForTransitionOrder ?? [],
      columnForNonTransitionOrder:
        pageSetting?.columnsForNonTransitionOrder ?? [],
      modelNameFilter,
      tableHeaderSubMenuMultiFilter: pageSetting.tableHeaderSubMenuMultiFilter,
      filteredKiTagIds,
      filteredSisTypeCodes,
      tableFilterItems: tableFilterSelectedItems || [],
      checkedKiListTable: checkedKiListTable || {},
      ...(dataModelTransitionGraphParams
        ? {
            dataModelTransitionGraph: {
              setting: dataModelTransitionGraphParams,
              selectKi,
            },
          }
        : {}),
      currentHalls,
      isHeatmapEnabled,
      selectedCurrentFieldCodes: currentFieldCodes,
      selectedCurrentFieldCode: currentFieldCodes.includes('all')
        ? undefined
        : currentFieldCodes.at(0),
      pagination,
      customDate: customDate ?? pageSetting.customDate,
      customWeek: customWeek ?? pageSetting.customWeek,
      customMonth: customMonth ?? pageSetting.customMonth,
    },
  };

  return result;
}

/**
 * お気に入り上書き保存時の処理
 */
function* saveFavoriteSaga(action: SaveModelTransitionReportsFavorite) {
  // 選択中のお気に入りID
  const selectedFavoriteId: number | undefined = yield select(
    modelTransitionReportsSelectedFavoriteSelector
  );
  // 選択中のお気に入り
  const selectedFavorite: FavoriteItem = yield select(
    modelTransitionReportsSelectedFavoriteDataSelector
  );
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  // デフォルトの時は動作しない
  if (selectedFavoriteId === undefined) return;

  // お気に入りのデータを生成
  if (action.payload.mode === 'memo') {
    // お気に入りを上書き
    yield put(
      SettingsFavoritesActionCreators.patchSettingsFavoritesAction(
        selectedFavoriteId,
        {
          ...selectedFavorite,
          memo: action.payload.memo,
        }
      )
    );
  } else {
    // お気に入りを上書き
    yield put(
      SettingsFavoritesActionCreators.patchSettingsFavoritesAction(
        selectedFavoriteId,
        {
          name: action.payload.name,
          isShared: action.payload.isShared,
          pageSetting,
          memo: action.payload.memo,
          privatelySharedUsers: action.payload.sharedUser,
        }
      )
    );
  }
}

/**
 * お気に入り新規保存時の処理
 */
function* saveAsFavoriteSaga(action: SaveModelTransitionReportsFavorite) {
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  // お気に入りを新規登録
  yield put(
    SettingsFavoritesActionCreators.postSettingsFavoritesAction([
      {
        name: action.payload.name,
        isShared: action.payload.isShared,
        pageName: '機種別推移',
        pageSetting,
        memo: action.payload.memo,
        privatelySharedUsers: action.payload.sharedUser,
      },
    ])
  );
}

/**
 * 機種別推移の全てのデータを破棄する
 */
function* initModelTransitionReportsDataSaga() {
  // 展開行
  yield put(
    DataModelTransition2ndRowActionCreators.renewDataModelTransition2ndRowAction()
  );
  // 閉じアニメーション途中の false のまま残ると次回オープンが壊れるためリセットする
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionGraphAnimationAction()
  );
}

/**
 * 検索ボタン押下時に行う処理
 */
export function* searchActionSaga(action: SearchModelTransitionReportsAction) {
  // 機種別推移のデータを初期化する
  yield fork(initModelTransitionReportsDataSaga);

  // MEMO: 検索条件の更新前に取得する（出力の変更判定に使う）
  const previousSearchCondition: DataModelTransitionParams = yield select(
    modelTransitionReportsSearchConditionSelector
  );

  // 現在の検索条件項目を取得
  const settingsOptions: SearchCondition = yield select(
    settingsOptionsModelTransitionSearchConditionSelector
  );

  // 現在選択中の種別
  const currentShu: ShuOption | undefined = yield select(
    modelTransitionReportsSelectedCurrentShuSelector
  );

  // すべての種別・種別グループ一覧
  const allShuOption: ShuOption[] = getShuOption(settingsOptions);
  // 現在選択可能な種別一覧
  const shuList =
    action.payload.selectedShu.length > 0
      ? action.payload.selectedShu
      : allShuOption;
  // currentShuが選択可能な種別一覧に含まれていない場合先頭を選択
  const checkedCurrentShu = checkCurrentShu(shuList, currentShu);

  // currentShuがselectedShu（選択可能な種別一覧）にない場合適切な種別を選択する
  if (
    currentShu !== undefined &&
    currentShu.type !== 'none' &&
    checkedCurrentShu !== undefined &&
    checkedCurrentShu.code !== currentShu.code
  ) {
    // currentShu（選択中の種別）を更新する
    yield put(
      ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsCurrentShuAction(
        checkedCurrentShu
      )
    );
  }

  // selectedShu（選択中の種別一覧）を更新する
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsShuAction(
      action.payload.selectedShu
    )
  );

  // 必要に応じて検索パラメータを加工する
  const baseModifyParams = queryParameterToSearchParams(
    checkedCurrentShu,
    action.payload.params
  );

  // MEMO: 出力変更時は非推移項目も出力別デフォルトに戻す
  // （SISタイプ・機種タグではタイプ/導入日を出さない）
  const nextMainField = resolveModelTransitionMainField(
    baseModifyParams.mainField
  );
  const isMainFieldChanged =
    resolveModelTransitionMainField(previousSearchCondition.mainField) !==
    nextMainField;
  const fields: SettingsOptionsModelTransition['fields'] = yield select(
    settingsOptionsModelTransitionFieldsSelector
  );
  const modifyParams = isMainFieldChanged
    ? {
        ...baseModifyParams,
        nonTransitiveFields: resolveModelTransitionDefaultNonTransitiveFields(
          nextMainField,
          fields.nonTransition
        ),
        transitiveFields: sanitizeModelTransitionSelectedFieldCodes(
          baseModifyParams.transitiveFields,
          fields.transition,
          nextMainField
        ),
      }
    : baseModifyParams;

  const dateType = action.payload.selectedDateType;
  const dateRange = action.payload.selectedDateRange;

  // dateType・dateRange（選択中の日付タイプ・期間）を更新する
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsDateRangeParamsAction(
      dateType,
      dateRange
    )
  );
  // searchCondition(選択中の検索条件）を更新する
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSearchConditionAction(
      modifyParams
    )
  );

  // フィルターされた機種リストの情報があれば削除
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsKiListAction(
      []
    )
  );

  // 選択中のSISリストに反映
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSisListAction(
      []
    )
  );

  // MEMO: 出力が変わると行の識別子（機種コード / SISタイプコード / 機種タグID）が
  // 別物になるため、前の出力のチェック・グラフ・展開行が新しい出力のパラメータに
  // 混ざらないよう破棄する（店舗変更・ソート・マーキングの再 fetch 経路を含む）
  if (isMainFieldChanged) {
    yield put(selectModelTransitionCheckedKiListTableAction({}));
    yield put(selectModelTransitionReportsModelNameFilterAction(''));
    yield put(selectModelTransitionReportsFilteredKiTagIdsAction([]));
    yield put(selectModelTransitionReportsFilteredSisTypeCodesAction([]));
    yield put(
      DataModelTransitionGraphActionCreators.renewDataModelTransitionGraphAction()
    );
    yield put(
      DataModelTransition2ndRowActionCreators.renewDataModelTransition2ndRowAction()
    );
    if (nextMainField !== MAIN_FIELD_TYPE.KI_TUSHO_MEI) {
      yield put(
        ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsIsHeatmapEnabledAction(
          false
        )
      );
    }
  }

  // データ取得
  yield put(
    DataModelTransitionActionCreators.fetchDataModelTransitionAction(
      checkedCurrentShu,
      modifyParams
    )
  );
}

/**
 * 検索ボタン押下時、新しい検索条件でデータを取得する
 */
function* handleSearchSaga() {
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_MODEL_TRANSITION_REPORTS,
    searchActionSaga
  );
}

/**
 * リセットボタン押下時、デフォルトの検索条件または選択中のお気に入りでデータを取得する
 */
function* resetSearchActionSaga() {
  // 現在選択中のお気に入りを取得する
  const favoriteId: number | undefined = yield select(
    modelTransitionReportsSelectedFavoriteSelector
  );

  // お気に入りが選択されている場合はお気に入り適用
  if (favoriteId !== undefined) {
    yield put(
      ModelTransitionReportsSettingActionCreators.changeModelTransitionReportsFavoriteAction(
        favoriteId
      )
    );
    return;
  }

  // 機種別推移のデータを初期化する
  yield fork(initModelTransitionReportsDataSaga);

  // ページネーションをリセット
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsPaginationAction(
      'main',
      0,
      50
    )
  );

  // 推移グラフを閉じる
  yield put(
    DataModelTransitionGraphActionCreators.renewDataModelTransitionGraphAction()
  );

  // デフォルトが選択されている場合は検索条件を初期化
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionReportsSearchConditionAction()
  );
  // 選択中の種別をリセットする
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionReportsShuAction()
  );

  // 選択中の期間をリセットする
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionReportsDateRangeParamsAction()
  );

  // 条件リセット時は「週次」で「期間7週」を選択状態にする
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsDateRangeParamsAction(
      'weekly',
      DATE_RANGE_XX_WEEK
    )
  );

  // フィルターされた機種リストの情報があれば削除
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsKiListAction(
      []
    )
  );
  // フィルターされた機種リストの情報があれば削除
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSisListAction(
      []
    )
  );
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionCurrentFieldAction(
      'all'
    )
  );

  // 現在の検索条件を取得
  const searchCondition: DataModelTransitionParams = yield select(
    modelTransitionReportsSearchConditionSelector
  );
  // 現在の種別を取得
  const currentShu: ShuOption = yield select(
    modelTransitionReportsSelectedCurrentShuSelector
  );
  // 現在の期間を取得
  const dateRangeParams: ModelTransitionReportsSettingDateRangeParams =
    yield select(modelTransitionReportsSelectedDateRangeParamsSelector);
  // 表示項目の選択肢データを取得
  const settingsOptions: SettingsOptionsModelTransition['fields'] =
    yield select(settingsOptionsModelTransitionFieldsSelector);

  // カスタム日数をリセット
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionCustomDateAction()
  );
  // カスタム週数をリセット
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionCustomWeekAction()
  );
  // カスタム月数をリセット
  yield put(
    ModelTransitionReportsSettingActionCreators.resetModelTransitionCustomMonthAction()
  );

  // リセット後のデフォルト値を使用
  const customDate = TEMPLATE_CONVERSION_CONFIG.daily.defaultValue;
  const customWeek = TEMPLATE_CONVERSION_CONFIG.weekly.defaultValue;
  const customMonth = TEMPLATE_CONVERSION_CONFIG.monthly.defaultValue;

  // 必要に応じて検索パラメータを加工する
  let modifyParams = updateSearchParamsWithDateRange(
    dateRangeParams,
    customDate,
    customWeek,
    customMonth,
    searchCondition
  );

  // 非推移項目のデフォルト項目を設定（出力によってタイプ・導入日の有無が変わる）
  if (!isExistNonTransitiveField(searchCondition)) {
    modifyParams = {
      ...modifyParams,
      nonTransitiveFields: resolveModelTransitionDefaultNonTransitiveFields(
        modifyParams.mainField,
        settingsOptions.nonTransition
      ),
    };
  }

  // セルの並び順をリセットしてデフォルト順にする
  yield put(selectModelTransitionReportsColumnsForTransitionAction([]));
  yield put(selectModelTransitionReportsColumnsForNonTransitionAction([]));

  // 検索条件を更新
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSearchConditionAction(
      modifyParams
    )
  );

  // 機種名フィルタをクリア
  yield put(selectModelTransitionReportsModelNameFilterAction(''));
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsTableHeaderSubMenuMultiFilterAction(
      {}
    )
  );
  // タグ絞り込みをクリア
  yield put(selectModelTransitionReportsFilteredKiTagIdsAction([]));
  // SISタイプ絞り込みをクリア
  yield put(selectModelTransitionReportsFilteredSisTypeCodesAction([]));

  // チェックされた機種をクリア
  yield put(selectModelTransitionCheckedKiListTableAction({}));

  // データ取得
  yield put(
    DataModelTransitionActionCreators.fetchDataModelTransitionAction(
      currentShu,
      modifyParams
    )
  );
}

/**
 * リセットボタン押下時機種別推移の全てのデータを破棄する
 */
function* handleResetSaga() {
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_RESET_MODEL_TRANSITION_REPORTS,
    resetSearchActionSaga
  );
}

/**
 * 変更後の種別でデータを取得する
 */
function* changeCurrentShuSaga(
  action: ChangeModelTransitionReportsCurrentShuAction
) {
  // 種別が選択されていない場合は処理を中断
  if (action.payload.shu === undefined) return;

  // 機種別推移のデータを初期化する
  yield fork(initModelTransitionReportsDataSaga);

  // 現在の検索条件を取得
  const searchCondition: DataModelTransitionParams = yield select(
    modelTransitionReportsSearchConditionSelector
  );

  // フィルターされた機種リストの情報があれば削除
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsKiListAction(
      []
    )
  ); // フィルターされた機種リストの情報があれば削除
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSisListAction(
      []
    )
  );

  // 表示項目の選択肢データを取得
  const settingsOptions: SettingsOptionsModelTransition['fields'] =
    yield select(settingsOptionsModelTransitionFieldsSelector);

  // 新しい検索条件
  const newSearchCondition = {
    ...searchCondition,
    // 初回取得時で週次、月次以外が選択されている場合はデフォルトでDK-SISを有効にする
    ...(searchCondition.dateType !== 'weekly'
      ? { sisFieldTypes: ['result'] }
      : {}),
    // 非推移項目のデフォルト項目を設定（出力によってタイプ・導入日の有無が変わる）
    ...(!isExistNonTransitiveField(searchCondition)
      ? {
          nonTransitiveFields: resolveModelTransitionDefaultNonTransitiveFields(
            searchCondition.mainField,
            settingsOptions.nonTransition
          ),
        }
      : {}),
  };

  // 検索条件を更新
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSearchConditionAction(
      newSearchCondition
    )
  );

  // 店舗フィルタで選択中の項目
  const currentHalls: string[] = yield select(
    modelTransitionReportsSettingCurrentHallsSelector
  );

  // 変更後の種別でデータを取得する
  yield put(
    DataModelTransitionActionCreators.fetchDataModelTransitionAction(
      action.payload.shu,
      {
        ...newSearchCondition,
        halls: resolveModelTransitionFetchHalls(
          currentHalls,
          searchCondition.halls
        ),
      }
    )
  );
}

/**
 * 選択中の種別変更時、データを取得する
 */
function* handleChangeCurrentShuSaga() {
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.CHANGE_MODEL_TRANSITION_REPORTS_CURRENT_SHU,
    changeCurrentShuSaga
  );
}

/**
 * 表示項目変更時データを取得する
 */
function* searchFieldsSaga(action: SearchModelTransitionReportsFieldsAction) {
  // 現在の機種別推移のデータを取得
  const modelTransition = yield* customSelect(dataModelTransitionSelector);
  // 非推移項目が空のときは、メインフィールドのみを指定する
  const nonTransitiveFields =
    action.payload.nonTransitiveFields.length === 0
      ? [resolveModelTransitionMainField(modelTransition?.setting.mainField)]
      : action.payload.nonTransitiveFields;
  const params = {
    nonTransitiveFields,
    transitiveFields: action.payload.transitiveFields,
  };

  // 項目絞込で選択されている項目
  const selectedCurrentFieldCodes = yield* customSelect(
    modelTransitionCurrentFieldCodesSelector
  );

  // 表示項目に、項目絞込で選択されている項目が存在するか確認
  const hasAnySelectedFieldInNewColumns =
    selectedCurrentFieldCodes.length === 0 ||
    selectedCurrentFieldCodes.includes('all') ||
    selectedCurrentFieldCodes.some((fieldCode) =>
      params.transitiveFields.includes(fieldCode)
    );

  if (!hasAnySelectedFieldInNewColumns) {
    yield put(
      ModelTransitionReportsSettingActionCreators.selectModelTransitionCurrentFieldAction(
        ['all']
      )
    );
  }

  yield fork(searchSearchConditionSaga, params);

  // パターン適用時の列順が指定されている場合は、それを反映する
  if (
    action.payload.columnsForTransitionOrder != null &&
    action.payload.columnsForTransitionOrder.length > 0
  ) {
    yield put(
      selectModelTransitionReportsColumnsForTransitionAction(
        action.payload.columnsForTransitionOrder
      )
    );
  }

  if (
    action.payload.columnsForNonTransitionOrder != null &&
    action.payload.columnsForNonTransitionOrder.length > 0
  ) {
    yield put(
      selectModelTransitionReportsColumnsForNonTransitionAction(
        action.payload.columnsForNonTransitionOrder
      )
    );
  }
}

/**
 * テーブル固有の検索条件を変更してデータを取得する
 * @param params 変更後の検索条件
 */
export function* searchSearchConditionSaga(params: DataModelTransitionParams) {
  // 機種別推移のデータを初期化する
  yield fork(initModelTransitionReportsDataSaga);

  // 現在の検索条件を取得
  const searchCondition = yield* customSelect(
    modelTransitionReportsSearchConditionSelector
  );
  // 現在の種別を取得
  const currentShu = yield* customSelect(
    modelTransitionReportsSelectedCurrentShuSelector
  );

  if (currentShu === undefined) {
    return;
  }

  // 店舗フィルタで選択中の項目
  const currentHalls = yield* customSelect(
    modelTransitionReportsSettingCurrentHallsSelector
  );

  // 新しい検索条件
  const newSearchCondition = {
    ...searchCondition,
    ...params,
    // 週次が DK-SIS に対応していないので、指定されていた場合は DK-SIS を OFF にする
    ...(searchCondition.dateType === 'weekly'
      ? { sisFieldTypes: undefined }
      : {}),
  };
  // 検索条件を更新
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsSearchConditionAction(
      newSearchCondition
    )
  );

  const hallsForFetch = resolveModelTransitionFetchHalls(
    currentHalls,
    searchCondition.halls
  );

  // データ取得
  yield put(
    DataModelTransitionActionCreators.fetchDataModelTransitionAction(
      currentShu,
      {
        ...newSearchCondition,
        halls: hallsForFetch,
      }
    )
  );

  const graphSearchCondition = yield* customSelect(
    dataModelTransitionGraphSettingSelector
  );

  // グラフデータ取得
  if (graphSearchCondition) {
    // WHY: テーブル fetch は currentShu で種別を上書きする。グラフも同じ解決済み種別を使う（P10）
    const graphParams = syncModelTransitionGraphParamsWithSearchCondition(
      graphSearchCondition,
      applyModelTransitionCurrentShuToSearchCondition(
        {
          ...newSearchCondition,
          halls: hallsForFetch,
        },
        currentShu
      )
    );
    yield put(
      DataModelTransitionGraphActionCreators.fetchDataModelTransitionGraphAction(
        graphParams
      )
    );
  }
}

/**
 * 非推移項目ソート実行時データを取得する
 */
function* searchSortForHeaderSaga(
  action: SearchModelTransitionReportsSortForHeaderAction
) {
  const params = {
    sortForHeader: action.payload.sort,
    order: action.payload.order,
  };
  yield fork(searchSearchConditionSaga, params);
}

/**
 * 推移項目ソート実行時データを取得する
 */
function* searchSortForTransitiveFieldSaga(
  action: SearchModelTransitionReportsSortForTransitiveFieldAction
) {
  // 現在の機種別推移のデータを取得
  const modelTransition: DataModelTransition = yield select(
    dataModelTransitionSelector
  );
  // 推移項目であるsortForTransitiveFieldを選択したとき、
  // sortForHeaderが日付項目でない場合にはデフォルト値として初日/週に変更する
  const sortForHeader = !modelTransition.data.dates
    .map((date) => date.code)
    .includes(modelTransition.setting.sortForHeader)
    ? modelTransition.data.dates.at(0)?.code
    : modelTransition.setting.sortForHeader;

  const params = {
    sortForHeader,
    sortForTransitiveField: action.payload.sort,
    order: action.payload.order,
  };

  yield fork(searchSearchConditionSaga, params);
}

/**
 * マーキング変更時データを取得する
 */
function* searchMarkingSaga(action: SearchModelTransitionReportsMarkingAction) {
  yield fork(searchSearchConditionSaga, {
    marking:
      action.payload.markingOption.code ===
      MODEL_REPORT_MARKING_CONDITIONS.at(0)?.code
        ? undefined
        : action.payload.markingOption.code,
    isFiltering: action.payload.isFiltering,
  });
}

/**
 * テーブル固有の検索条件変更検知時の処理
 */
function* handleTableSearchSaga() {
  // 非推移項目ソート実行時
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_MODEL_TRANSITION_REPORTS_SORT_FOR_HEADER,
    searchSortForHeaderSaga
  );

  // 推移項目ソート実行時
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_MODEL_TRANSITION_REPORTS_SORT_FOR_TRANSITIVE_FIELD,
    searchSortForTransitiveFieldSaga
  );

  // マーキング実行時
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_MODEL_TRANSITION_REPORTS_MARKING,
    searchMarkingSaga
  );

  // 表示項目変更時
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_MODEL_TRANSITION_REPORTS_FIELDS,
    searchFieldsSaga
  );
}

/**
 * 表示項目の並び順を変更する
 */
export function* changeColumnsOrderSaga() {
  const fieldsForTransition: Column[] = yield select(
    dataModelTransitionColumnsForTransitionSelector
  );
  const fieldsForNonTransition: Column[] = yield select(
    dataModelTransitionColumnsForNonTransitionSelector
  );
  const setting: DataModelTransitionParams | undefined = yield select(
    dataModelTransitionSettingsSelector
  );
  const mainFieldColumnCode = resolveModelTransitionMainField(
    setting?.mainField
  );

  // テーブル列の並び順
  const columnsForTransitionOrder: string[] = yield select(
    modelTransitionReportsColumnsForTransitionOrderSelector
  );
  const columnsForNonTransitionOrder: string[] = yield select(
    modelTransitionReportsColumnsForNonTransitionOrderSelector
  );

  // MEMO: 推移項目の DK-SIS 項目の存在チェック
  const existTransitionDkSis = columnsForTransitionOrder
    .join('_')
    .indexOf('_sis');

  const orderedForTransitionCode = sortBy(fieldsForTransition, (column) => {
    // DK-SIS項目を新規で追加する場合は強制で末尾に配置する
    if (existTransitionDkSis === -1 && column.isSisField) {
      return Infinity;
    }
    // columnsForTransitionOrder に従って順番を決定
    return columnsForTransitionOrder.indexOf(column.code);
  }).map((column) => column.code);

  // MEMO: 非推移項目の DK-SIS 項目の存在チェック
  const existNonTransitionDkSis = columnsForNonTransitionOrder
    .join('_')
    .indexOf('_sis');

  const orderedForNonTransitionCode = sortBy(
    fieldsForNonTransition,
    (column) => {
      // mainField 列は常に先頭
      if (column.code === mainFieldColumnCode) {
        return -Infinity;
      }
      // DK-SIS項目を新規で追加する場合は強制で末尾に配置する
      if (existNonTransitionDkSis === -1 && column.isSisField) {
        return Infinity;
      }
      // columnsForNonTransitionOrder に従って順番を決定
      return columnsForNonTransitionOrder.indexOf(column.code);
    }
  ).map((column) => column.code);

  yield put(
    selectModelTransitionReportsColumnsForTransitionAction(
      orderedForTransitionCode
    )
  );

  yield put(
    selectModelTransitionReportsColumnsForNonTransitionAction(
      orderedForNonTransitionCode
    )
  );
}

/**
 * お気に入り関連のActionがDispatchされるのを監視
 */
function* handleFavoriteSaga() {
  // お気に入り選択
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.CHANGE_MODEL_TRANSITION_REPORTS_FAVORITE,
    applyFavoriteByIdSaga
  );
  // お気に入り上書き保存
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SAVE_MODEL_TRANSITION_REPORTS_FAVORITE,
    saveFavoriteSaga
  );
  // お気に入り新規保存
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SAVE_AS_MODEL_TRANSITION_REPORTS_FAVORITE,
    saveAsFavoriteSaga
  );
}

// 期間スライドコンポーネントがクリックされた時に検索期間を設定して再検索を実行する
function* searchDateRangeSlideSaga(
  action: SearchModelTransitionReportsDateRangeSlideAction
) {
  const { startDate, endDate } = action.payload;

  // 期間スライド時に期間選択状態を「カスタム」に変更
  const { dateType } = yield* customSelect(
    modelTransitionReportsSelectedDateRangeParamsSelector
  );
  yield put(
    ModelTransitionReportsSettingActionCreators.selectModelTransitionReportsDateRangeParamsAction(
      dateType,
      CUSTOM_DATE_RANGE
    )
  );
  yield fork(searchSearchConditionSaga, { startDate, endDate });
}

// 期間スライドコンポーネントがクリックされた時に実行する処理
function* handleDateRangeSlideSaga() {
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.SEARCH_MODEL_TRANSITION_REPORTS_DATE_RANGE_SLIDE,
    searchDateRangeSlideSaga
  );
}

/**
 * 変更後の店舗でデータを取得する
 */
export function* changeCurrentHallsSaga(
  action: ChangeModelTransitionReportsCurrentHallsAction
) {
  // 店舗が選択されていない場合は処理を中断
  if (action.payload.halls === undefined) return;

  yield fork(searchSearchConditionSaga, {});
}

/**
 * 選択中の店舗変更時、データを取得する
 */
function* handleChangeCurrentHallsSaga() {
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.CHANGE_MODEL_TRANSITION_REPORTS_CURRENT_HALLS,
    changeCurrentHallsSaga
  );
}

/**
 * 画面共有用短縮URL作成の処理
 */
function* createShortenedUrlSaga(
  action: CreateModelTransitionReportsShortenedUrlAction
) {
  const pageSetting: FavoriteItem['pageSetting'] = yield call(setPageSetting);

  const compressed = compressToEncodedURIComponent(
    action.payload.pageName,
    pageSetting ?? {}
  );
  const originalUrl = `${action.payload.locationUrl}?q=${compressed}`;

  yield put(ShortenedUrlActionCreators.postShortenedUrlAction({ originalUrl }));
}

// 画面共有用短縮URL作成の処理
function* handleShortenedUrlSaga() {
  yield takeEvery(
    ModelTransitionReportsSettingActionTypes.CREATE_MODEL_TRANSITION_REPORTS_SHORTENED_URL,
    createShortenedUrlSaga
  );
}

/**
 * 機種別推移の検索フォームに関するタスクを実行する
 */
export function* modelTransitionReportsSettingSaga() {
  yield fork(handleSearchSaga);
  yield fork(handleResetSaga);
  yield fork(handleTableSearchSaga);
  yield fork(handleChangeCurrentShuSaga);
  yield fork(handleFavoriteSaga);
  yield fork(handleDateRangeSlideSaga);
  yield fork(handleChangeCurrentHallsSaga);
  yield fork(handleShortenedUrlSaga);
}
